import _ from "lodash";

// Utility constants and helpers for AIPlayground

export const MANUAL_TOOL_HANDLER = {
  value: "manual",
  label: "Manual (No tool call)",
  toolHandler: "manual",
};

export const REASONING_EFFORT_TOKENS_MAP = {
  NONE: 0,
  LOW: 256,
  MEDIUM: 512,
  HIGH: 1024,
};

export const getRandomId = (prefix = "user") =>
  `${prefix}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Build tool handler parameters array dynamically from CMS tools.
 * @param {Array} cmsTools - Array of CMS tool objects (from GraphQL)
 * @returns {Array} Array of tool handler parameter objects
 */
export function getToolHandlerParametersFromCms(cmsTools = []) {
  return _.map(cmsTools, tool => {
    let schemaObj = {};
    try {
      schemaObj = _.isString(tool.schema)
        ? JSON.parse(tool.schema)
        : tool.schema;
    } catch {
      schemaObj = {};
    }
    const properties = _.omit(_.get(schemaObj, "properties", {}), [
      "description",
    ]);
    const required = _.get(schemaObj, "required", []);
    const allParams = Object.keys(properties);
    const key = tool.version ? `${tool.name}_v${tool.version}` : tool.name;
    return {
      name: tool.name,
      key,
      mandatoryParams: required,
      optionalParams: _.difference(allParams, required),
      toolHandler: tool.toolHandler,
    };
  });
}

/**
 * Get parameters from a JSON schema
 * @param {Object|string} schema - The tool schema (object or JSON string)
 * @returns {Object} - Object containing requiredParams and optionalParams arrays
 */
export const getSchemaParameters = schema => {
  try {
    const parsedSchema = _.isString(schema) ? JSON.parse(schema) : schema;
    const properties = _.get(parsedSchema, "properties", {});
    const required = _.get(parsedSchema, "required", []);

    const allParams = _.keys(properties);
    const requiredParams = _.filter(allParams, param =>
      _.includes(required, param)
    );
    const optionalParams = _.filter(
      allParams,
      param => !_.includes(required, param)
    );

    return { requiredParams, optionalParams };
  } catch {
    return { requiredParams: [], optionalParams: [] };
  }
};

/**
 * Check if a tool handler is compatible with the given schema parameters
 * @param {Object} toolHandlerConfig - The tool handler configuration
 * @param {Array} schemaRequiredParams - Required parameters from schema
 * @param {Array} schemaOptionalParams - Optional parameters from schema
 * @returns {boolean} - Whether the tool handler is compatible
 */
export const isToolHandlerCompatible = (
  toolHandlerConfig,
  schemaRequiredParams,
  schemaOptionalParams
) => {
  const { mandatoryParams = [] } = toolHandlerConfig;
  const allSchemaParams = [...schemaRequiredParams, ...schemaOptionalParams];

  // Check if all mandatory params of the handler are present in schema
  const hasMandatoryParams = _.every(mandatoryParams, param =>
    _.includes(allSchemaParams, param)
  );

  // Check if schema required params are a subset of handler's mandatory params
  const requiredParamsMatch = _.every(schemaRequiredParams, param =>
    _.includes(mandatoryParams, param)
  );

  return hasMandatoryParams && requiredParamsMatch;
};

/**
 * Get compatible tool handlers based on the tool schema
 * @param {Object|string} schema - The tool schema
 * @param {string} currentToolHandler - The current tool handler from CMS
 * @returns {Array} - Array of compatible tool handler options for dropdown
 */
export const getCompatibleToolHandlers = (
  schema,
  currentToolHandler,
  handlerParameters = []
) => {
  const { requiredParams, optionalParams } = getSchemaParameters(schema);
  const compatibleHandlers = [];
  const addedHandlers = new Set();

  // Check if current tool handler from CMS is still compatible with the schema
  if (!_.isEmpty(currentToolHandler) && currentToolHandler !== "manual") {
    const currentHandler = _.find(
      handlerParameters,
      handler => handler.toolHandler === currentToolHandler
    );
    if (
      currentHandler &&
      !addedHandlers.has(currentHandler.toolHandler) &&
      isToolHandlerCompatible(currentHandler, requiredParams, optionalParams)
    ) {
      compatibleHandlers.push({
        value: currentHandler.toolHandler,
        label: `${currentHandler.name} (Current)`,
        toolHandler: currentHandler.toolHandler,
      });
      addedHandlers.add(currentHandler.toolHandler);
    }
  }

  // Find other compatible handlers based on schema
  _.forEach(handlerParameters, handler => {
    if (addedHandlers.has(handler.toolHandler)) {
      return;
    }

    if (isToolHandlerCompatible(handler, requiredParams, optionalParams)) {
      compatibleHandlers.push({
        value: handler.toolHandler,
        label: handler.name,
        toolHandler: handler.toolHandler,
      });
      addedHandlers.add(handler.toolHandler);
    }
  });

  // Always add the Manual option at the end
  compatibleHandlers.push(MANUAL_TOOL_HANDLER);

  return compatibleHandlers;
};

/**
 * Processes parsed messages from streaming to extract tool call and tool response messages.
 * This is used when AI makes tool calls during streaming - we need to create
 * separate messages for the assistant's tool call and the tool's response.
 *
 * @param {Object} params - The function parameters
 * @param {Array} params.parsedMessages - Array of parsed messages from streaming
 * @param {Array} params.savedToolUseIds - Array of already saved tool use IDs
 * @param {string} params.usageMetricsId - The usage metrics ID
 * @param {string} params.parentMessageId - The parent message ID
 * @param {Object} params.lastAssistantMessage - The last assistant message with tool calls
 * @returns {Object} - Object containing messagesToAdd, newToolUseIds, and latestAssistantMessage
 */
export const getToolMessagesFromParsedMessages = params => {
  const {
    parsedMessages = [],
    savedToolUseIds = [],
    usageMetricsId,
    parentMessageId,
    lastAssistantMessage,
  } = params || {};

  if (_.isEmpty(parsedMessages)) {
    return {
      messagesToAdd: [],
      newToolUseIds: [],
      latestAssistantMessage: null,
    };
  }

  const toolUseIds = _.chain(parsedMessages)
    .map("message.toolCallId")
    .compact()
    .value();

  if (_.isEmpty(toolUseIds)) {
    return {
      messagesToAdd: [],
      newToolUseIds: [],
      latestAssistantMessage: null,
    };
  }

  const flatMessages = _.chain(parsedMessages)
    .filter(item => _.has(item, "message"))
    .map("message")
    .value();

  const newToolUseIds = _.difference(toolUseIds, savedToolUseIds);
  if (_.isEmpty(newToolUseIds)) {
    return {
      messagesToAdd: [],
      newToolUseIds: [],
      latestAssistantMessage: null,
    };
  }

  const messagesToAdd = [];
  let latestAssistantMessage = null;

  if (_.size(newToolUseIds) > 0) {
    let toolCallMessage = null;
    if (!_.isNil(lastAssistantMessage)) {
      const toolCallIds = _.map(
        _.get(lastAssistantMessage, "metadata.toolCalls", []),
        "id"
      );
      if (_.intersection(toolCallIds, newToolUseIds).length === 0) {
        toolCallMessage = _.find(flatMessages, item => {
          const itemToolCallIds = _.map(item?.toolCalls, "id");
          return _.intersection(itemToolCallIds, newToolUseIds).length > 0;
        });
      } else {
        // Last assistant message has made multiple tool calls, and this new tool call is part of it.
        latestAssistantMessage = lastAssistantMessage;
      }
    } else {
      toolCallMessage = _.find(flatMessages, item => {
        const itemToolCallIds = _.map(item?.toolCalls, "id");
        return _.intersection(itemToolCallIds, newToolUseIds).length > 0;
      });
    }

    if (!_.isNil(toolCallMessage)) {
      const combinedToolCalls = _.get(toolCallMessage, "toolCalls", []);
      latestAssistantMessage = {
        authorType: "assistant",
        content: _.get(toolCallMessage, "content", ""),
        usageMetricsId,
        metadata: {
          toolCalls: combinedToolCalls,
        },
        parentMessageId,
      };
      messagesToAdd.push(_.cloneDeep(latestAssistantMessage));
    }
  }

  _.forEach(newToolUseIds, toolUseId => {
    // tool message corresponding to this tool call id
    const toolResponseParsedMessage = _.find(flatMessages, item => {
      const { role, toolCallId } = item || {};
      return role === "tool" && toolCallId === toolUseId;
    });

    const toolCallId = _.get(toolResponseParsedMessage, "toolCallId");
    if (!_.isEmpty(toolCallId)) {
      // tool node with toolResponse and toolCallId
      messagesToAdd.push({
        authorType: "tool",
        content: _.get(toolResponseParsedMessage, "toolResponse"),
        metadata: {
          toolCallId,
        },
      });
    }
  });

  return { messagesToAdd, newToolUseIds, latestAssistantMessage };
};

// Direct mapping from aiUsageMetrics model names to aiConversation model names
const MODEL_NAME_MAP = {
  "gemini-2.5-flash": "vertexai:gemini-2-5-flash",
  "gemini-2.5-pro": "vertexai:gemini-2-5-pro",
  "gemini-2.0-flash": "vertexai:gemini-2-0-flash",
  "gemini-2-5-flash": "vertexai:gemini-2-5-flash",
  "gemini-2-5-pro": "vertexai:gemini-2-5-pro",
  "gemini-2-0-flash": "vertexai:gemini-2-0-flash",

  "gpt-4o": "openai:gpt-4o",
  "gpt-4.1": "openai:gpt-4.1",
  "gpt-4.1-mini": "openai:gpt-4.1-mini",
  "gpt-4.1-nano": "openai:gpt-4.1-nano",
  "gpt-5": "openai:gpt-5",
  o3: "openai:o3",
  o1: "openai:o1",

  "claude-3-haiku": "bedrock:claude-3-haiku",
  "claude-3.5-sonnet": "bedrock:claude-3.5-sonnet",
  "claude-3.5-sonnet-v2": "bedrock:claude-3.5-sonnet-v2",
  "claude-3.7-sonnet": "bedrock:claude-3.7-sonnet",
  "claude-4-sonnet": "bedrock:claude-4-sonnet",
  "claude-4.5-sonnet": "bedrock:claude-4.5-sonnet",
};

export const formatModelWithProvider = model => {
  if (_.isEmpty(model)) return "openai:gpt-4o";
  if (_.includes(model, ":")) return model;

  const mappedModel = _.get(MODEL_NAME_MAP, model);
  if (mappedModel) {
    return mappedModel;
  }
  const lowerModel = _.toLower(model);
  const lowerMappedModel = _.get(MODEL_NAME_MAP, lowerModel);
  if (lowerMappedModel) {
    return lowerMappedModel;
  }
  return `openai:${model}`;
};

const extractMessageContent = content => {
  if (_.isString(content)) return content;

  if (_.isArray(content)) {
    return _.chain(content)
      .map(item => {
        switch (_.get(item, "type")) {
          case "text":
            return _.get(item, "text", "");
          case "image_url":
            return _.get(item, "image_url.url", "");
          case "audio_url":
            return _.get(item, "audio_url.url", "");
          default:
            return "";
        }
      })
      .compact()
      .join("\n")
      .value();
  }

  return "";
};

export const transformAiUsageMetricsToPlayground = aiUsageMetrics => {
  if (_.isEmpty(aiUsageMetrics)) {
    return null;
  }

  const inputText = _.get(aiUsageMetrics, "inputText", {});
  const responseText = _.get(aiUsageMetrics, "responseText", {});
  const metadata = _.get(aiUsageMetrics, "metadata", {});
  const messages = _.get(inputText, "messages", []);

  const systemMessage = _.find(messages, { role: "system" });
  const systemPrompt = extractMessageContent(_.get(systemMessage, "content"));

  const rawModel = _.get(aiUsageMetrics, "model", "gpt-4o");
  const model = formatModelWithProvider(rawModel);
  const temperature = parseFloat(_.get(aiUsageMetrics, "temperature", 0.7));
  const validTemperature = Math.min(1, Math.max(0, temperature || 0.7));

  const promptToolNames = _.get(
    aiUsageMetrics,
    "prompt.metadata.toolNames",
    []
  );
  const tools = !_.isEmpty(promptToolNames)
    ? promptToolNames
    : _.map(_.get(metadata, "tools", []), tool => {
        const name = _.get(tool, "name", "");
        const version = _.get(tool, "version", "");
        return version ? `${name}_v${version}` : name;
      });

  const chatMessages = _.filter(messages, msg => msg.role !== "system");
  const messageBlocks = _.map(chatMessages, (msg, idx) => {
    const role = _.get(msg, "role", "user");
    const content = extractMessageContent(_.get(msg, "content", ""));
    const toolCalls = _.get(msg, "tool_calls", []);
    const usageMetrics = _.get(msg, "usageMetrics", {});
    const toolCallId = _.get(msg, "tool_call_id", null);

    return {
      id: `NEW_${Date.now()}_${idx}`,
      authorType: role,
      content,
      metadata: {
        isUserInputMessage: role === "user",
        tool_calls: toolCalls,
        toolCalls: toolCalls,
        toolCallId,
        usageMetricsId: _.get(usageMetrics, "id", null),
        totalToolExecutionTime: _.get(
          usageMetrics,
          "totalToolExecutionTime",
          null
        ),
        totalChunkTime: _.get(msg, "totalChunkTime", null),
      },
    };
  });
  if (!_.isEmpty(responseText)) {
    const responseContent = extractMessageContent(
      _.get(responseText, "content", "")
    );
    const reasoning = _.get(responseText, "reasoning", null);
    if (!_.isEmpty(responseContent)) {
      messageBlocks.push({
        id: `NEW_${Date.now()}_response`,
        authorType: "assistant",
        content: responseContent,
        metadata: {
          isUserInputMessage: false,
          reasoning,
        },
      });
    }
  }

  return {
    conversationDetails: {
      model,
      systemPrompt,
      temperature: validTemperature,
      tools,
      toolChoice: "auto",
      reasoningEffort: "NONE",
    },
    messages: messageBlocks,
    debugInfo: {
      usageMetricsId: _.get(aiUsageMetrics, "id"),
      promptTokens: _.get(aiUsageMetrics, "promptTokens"),
      completionTokens: _.get(aiUsageMetrics, "completionTokens"),
      cachedPromptTokens: _.get(aiUsageMetrics, "cachedPromptTokens"),
      totalRequestTime: _.get(metadata, "totalRequestTime"),
      sourceType: _.get(aiUsageMetrics, "type"),
      createdAt: _.get(aiUsageMetrics, "createdAt"),
    },
  };
};

export const isValidUsageMetricsId = usageMetricsId => {
  return !_.isEmpty(usageMetricsId) && !_.isNil(usageMetricsId);
};
